import { Injectable } from '@angular/core';
import { IGradeV2 } from '../../shared/models/igrade-v2';

@Injectable({
  providedIn: 'root'
})
export class GradeMapperService {

  constructor() { }

  /**
   * Mapping functions keyed by a string identifier.
   * Each function takes an array of grades (IGradeV2[]) and returns a transformed array.
   */
  private mappingFunctions: { [key: string]: (grades: IGradeV2[]) => any[] } = {

    /**
     * Maps grades to include:
     * - classesCount: number of classes in the grade
     * - studentsCount: total number of students across all classes
     */
    gradeWithClassStudentsCounts: (grades: IGradeV2[]) =>
      grades.map(grade => {
        const missingInfo = this.getMissingInfo(grade);
        return {
          ...grade,
          classesCount: grade?.school_classes?.length || 0,
          studentsCount:
            grade?.school_classes?.reduce((total, schoolClass) => {
              return total + (schoolClass?.students?.length || 0);
            }, 0) || 0,
          missingInfo,
          hasMissingInfo: missingInfo.length > 0,
        };
      }),

    /**
     * Transforms grades and their classes to a format suitable for PrimeNG dropdowns,
     * filtering out grades with no classes.
     * Each grade object contains:
     * - name, id
     * - school_classes: array of { name, id } objects only
     */
    transformGradeAndClassForPrime: (grades: IGradeV2[]) =>
      grades.reduce((acc: any[], grade) => {
        if (grade.school_classes && grade.school_classes.length > 0) {
          acc.push({
            name: grade.name,
            id: grade.id,
            school_classes: grade.school_classes.map(schoolClass => ({
              name: schoolClass.name,
              id: schoolClass.id,
            })),
          });
        }
        return acc;
      }, []),
  };

  /**
   * Applies the specified mapping function to the provided grades array.
   * 
   * @param grades - The array of grades to transform.
   * @param type - The key identifying which mapping function to apply.
   *               If the type is not found, returns the original grades array.
   * @returns The transformed array of grades.
   */
  map(grades: IGradeV2[], type: string): any[] {
    const fn = this.mappingFunctions[type];
    return fn ? fn(grades) : grades;
  }

  /**
   * Returns which of flow/gates/teachers are missing for a grade, based on the
   * has_* flags returned by the all-grades endpoint.
   * A grade is missing "teachers" if any of its classes has has_teachers false.
   * Supervisors are deliberately not flagged: the role is optional and schools
   * are discouraged from using it, so a missing supervisor is not a problem.
   */
  private getMissingInfo(grade: IGradeV2): string[] {
    const missing: string[] = [];
    if (!grade?.has_flow) {
      missing.push('flow');
    }
    if (!grade?.has_gates) {
      missing.push('gates');
    }
    const hasClassWithoutTeachers = (grade?.school_classes || []).some(
      schoolClass => !schoolClass?.has_teachers
    );
    if (hasClassWithoutTeachers) {
      missing.push('teachers');
    }
    return missing;
  }
}
